import { useEffect, useState } from "react";
import { api, formatDate } from "../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { TrendUp } from "@phosphor-icons/react";

export default function BDPerformance() {
  const [rows, setRows] = useState([]);
  const [weeks, setWeeks] = useState("8");

  useEffect(() => {
    api.get("/bd/performance", { params: { weeks: parseInt(weeks) } }).then(r => setRows(r.data));
  }, [weeks]);

  // group by week_ending
  const weekGroups = {};
  rows.forEach(r => {
    weekGroups[r.week_ending] = weekGroups[r.week_ending] || [];
    weekGroups[r.week_ending].push(r);
  });

  // totals by rep
  const repTotals = {};
  rows.forEach(r => {
    const t = repTotals[r.bd_rep] = repTotals[r.bd_rep] || { bd_rep: r.bd_rep, cold_contacts: 0, consultations: 0, assessments: 0, loi_sent: 0, loi_signed: 0, sites_activated: 0, total_activities: 0 };
    ["cold_contacts","consultations","assessments","loi_sent","loi_signed","sites_activated","total_activities"].forEach(k => { t[k] += r[k] || 0; });
  });
  const repList = Object.values(repTotals).sort((a,b) => b.total_activities - a.total_activities);

  return (
    <div className="p-8 max-w-[1400px]" data-testid="bd-performance-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-[#5C5E66] mb-1">BD Pipeline</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight">BD Performance</h1>
          <p className="text-sm text-[#5C5E66] mt-2">Weekly activity metrics rolled up from the Activity Log. Track outbound volume, conversion at each stage, and site activations per BD Rep.</p>
        </div>
        <Select value={weeks} onValueChange={setWeeks}>
          <SelectTrigger data-testid="weeks-select" className="rounded-none w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="4">Last 4 weeks</SelectItem>
            <SelectItem value="8">Last 8 weeks</SelectItem>
            <SelectItem value="13">Last quarter</SelectItem>
            <SelectItem value="26">Last 6 months</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Rep totals */}
      <div className="mb-10">
        <div className="text-xs font-mono uppercase tracking-wider text-[#5C5E66] mb-3 flex items-center gap-2"><TrendUp size={14} weight="bold" className="text-[#FF9900]" /> Cumulative — Last {weeks} weeks</div>
        <div className="bg-white border border-[#E2E2E5]" data-testid="rep-totals">
          <div className="grid grid-cols-[1.2fr_repeat(7,1fr)] gap-0 border-b border-[#E2E2E5] bg-[#F0F0F2]">
            <Th>BD Rep</Th><Th>Cold Contacts</Th><Th>Consultations</Th><Th>Assessments</Th><Th>LOI Sent</Th><Th>LOI Signed</Th><Th>Activated</Th><Th>Total</Th>
          </div>
          {repList.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#5C5E66]">No activities in this window yet</div>
          ) : repList.map(r => (
            <div key={r.bd_rep} className="grid grid-cols-[1.2fr_repeat(7,1fr)] gap-0 border-b border-[#E2E2E5] last:border-0 row-hover" data-testid={`rep-row-${r.bd_rep}`}>
              <Td className="font-semibold text-sm">{r.bd_rep}</Td>
              <Td className="font-mono tabular-nums">{r.cold_contacts}</Td>
              <Td className="font-mono tabular-nums">{r.consultations}</Td>
              <Td className="font-mono tabular-nums">{r.assessments}</Td>
              <Td className="font-mono tabular-nums">{r.loi_sent}</Td>
              <Td className="font-mono tabular-nums text-[#FF9900]">{r.loi_signed}</Td>
              <Td className="font-mono tabular-nums text-[#2E7D32]">{r.sites_activated}</Td>
              <Td className="font-mono tabular-nums font-semibold">{r.total_activities}</Td>
            </div>
          ))}
        </div>
      </div>

      {/* Weekly breakdown */}
      <div>
        <div className="text-xs font-mono uppercase tracking-wider text-[#5C5E66] mb-3">Weekly Breakdown</div>
        <div className="bg-white border border-[#E2E2E5]" data-testid="weekly-table">
          <div className="grid grid-cols-[140px_1.2fr_repeat(7,1fr)] gap-0 border-b border-[#E2E2E5] bg-[#F0F0F2]">
            <Th>Week Ending</Th><Th>BD Rep</Th><Th>Cold</Th><Th>Consult.</Th><Th>Assess.</Th><Th>LOI Sent</Th><Th>LOI Signed</Th><Th>Activated</Th><Th>Total</Th>
          </div>
          {Object.keys(weekGroups).sort().reverse().map(week => (
            weekGroups[week].map((r, i) => (
              <div key={`${week}-${r.bd_rep}`} className="grid grid-cols-[140px_1.2fr_repeat(7,1fr)] gap-0 border-b border-[#E2E2E5] last:border-0 row-hover">
                <Td className="font-mono text-xs">{i === 0 ? formatDate(week) : ""}</Td>
                <Td className="text-sm font-medium">{r.bd_rep}</Td>
                <Td className="font-mono tabular-nums">{r.cold_contacts}</Td>
                <Td className="font-mono tabular-nums">{r.consultations}</Td>
                <Td className="font-mono tabular-nums">{r.assessments}</Td>
                <Td className="font-mono tabular-nums">{r.loi_sent}</Td>
                <Td className="font-mono tabular-nums text-[#FF9900]">{r.loi_signed}</Td>
                <Td className="font-mono tabular-nums text-[#2E7D32]">{r.sites_activated}</Td>
                <Td className="font-mono tabular-nums font-semibold">{r.total_activities}</Td>
              </div>
            ))
          ))}
          {rows.length === 0 && <div className="p-8 text-center text-sm text-[#5C5E66]">No weekly data yet — log activities to populate this report</div>}
        </div>
      </div>
    </div>
  );
}

const Th = ({ children }) => <div className="px-3 py-2.5 text-[10px] font-mono uppercase tracking-wider text-[#5C5E66]">{children}</div>;
const Td = ({ children, className = "" }) => <div className={`px-3 py-3 ${className}`}>{children}</div>;
